import { ChangeDetectionStrategy, Component, EventEmitter, Inject, Input, Output } from '@angular/core';
import { ChatMessage, FeedbackType, MessageFunctionType, SenderType } from '../../../digital-assistant.model';
import { ComponentMappingService } from '../services/component-mapping.service';
import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import moment from 'moment';
import { CbreAlertService } from '@cbre/alerts';
import { L10nTranslationService } from 'angular-l10n';
import { ExcelColumnStylesConfig } from './excel-column-styles.config';
import { UserActionIds, UserActionTrackerService } from '@miq/utils/metrics';
import { CbreLogger, CbreLoggerToken } from '@cbre/logger';

@Component({
  selector: 'miq-chat-conversation',
  templateUrl: './chat-bot-conversation.component.html',
  styleUrls: ['./chat-bot-conversation.component.scss'],
  providers: [ComponentMappingService],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ChatBotConversationComponent {
  public isCopyIconDisabled = false;
  private _message: ChatMessage | null = null;
  public isUser = false;
  public isBot = false;
  public MessageFunctionType = MessageFunctionType;
  public feedbackType = FeedbackType;
  @Output() emitFeedback = new EventEmitter<boolean>();
  @Input() index = 0;

  @Input()
  set message(value: ChatMessage | null) {
    this._message = value;
    this.isUser = value?.from == SenderType.User;
    this.isBot = value?.from == SenderType.Bot;
  }

  get message(): ChatMessage | null {
    return this._message;
  }

  constructor(
    private cbreAlertService: CbreAlertService,
    private translationService: L10nTranslationService,
    private userActionTrackerService: UserActionTrackerService,
    @Inject(CbreLoggerToken) private readonly logger: CbreLogger
  ) {}

  download(message: Record<string, any>): void {
    if (this.isCopyIconDisabled) {
      return;
    }
    if (message && message.type === MessageFunctionType.Table) {
      if (!message.content?.columns || !message.content?.rows) {
        this.logger.error('Invalid table data');
        return;
      }
      this.isCopyIconDisabled = true;

      this.cbreAlertService.showInfoToast(this.translationService.translate('chat.chatDownloadInProgress'));
      // Create a new workbook and worksheet
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet(`${message.content.title || 'chatBotResponse'}`);

      // Add a summary above the table
      if (message.content?.summary) {
        worksheet.addRow([`Summary:`, message.content.summary]);
        // Add multiple empty rows after the summary
        worksheet.addRows([[], [], []]);
      }

      // Add the column headers
      const headerRow = worksheet.addRow(message.content.columns);
      // Style the header row and data cells
      const headerStyle = ExcelColumnStylesConfig.header;
      headerRow.eachCell(cell => {
        // Apply header styles from configuration
        Object.keys(headerStyle).forEach(styleKey => {
          (cell as any)[styleKey] = headerStyle[styleKey];
        });
      });

      // Apply styles to data rows
      // Apply styles to data rows
      const rows = message.content?.rows || [];
      for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
        const row = rows[rowIndex];
        const dataRow = worksheet.getRow(rowIndex + headerRow.number + 1); // Data rows start after the header row
        for (let colIndex = 0; colIndex < row.length; colIndex++) {
          let cellValue = row[colIndex];
          const cell = dataRow.getCell(colIndex + 1);

          // Set cell value
          if (!isNaN(cellValue) && cellValue !== null && cellValue !== '') {
            cellValue = Number(cellValue); // Convert numeric strings to numbers
          }
          cell.value = cellValue;
        }
      }

      // Generate the Excel file and trigger the download
      workbook.xlsx
        .writeBuffer()
        .then(data => {
          const blob = new Blob([data]);
          const timestamp = moment.utc().format('YYYY-MM-DD_HH-mm-ss');
          saveAs(blob, `${message.content.title || 'chatBotResponse'}-${timestamp}.xlsx`);
          this.trackUserAction('Downloaded chatbot response', message);
        })
        .catch(error => {
          this.logger.error(`Error generating Excel file: ${error}`);
        });
      this.isCopyIconDisabled = false;
    }
  }

  giveFeedback(feedback: boolean): void {
    this.emitFeedback.emit(feedback);
  }

  private trackUserAction(actionTaken: string, message: Record<string, any>): void {
    const actionId = UserActionIds.DigitalAssistant;
    const actionDetails = {
      actionId,
      actionTaken: actionTaken,
      message: {
        id: message.id,
        contentSummary: message.content?.summary || '',
        type: message.type,
      },
    };
    this.userActionTrackerService.registerAction({ actionId, actionDetails });
  }
}
